// Compressão de imagens no próprio navegador, antes de qualquer upload.
// O acervo de fotos cresce rápido (uma apreciação junta centenas de imagens),
// então cada arquivo é reduzido até caber no teto definido aqui.

import { converterHeicParaJpeg } from './heic-converter';

/** Teto de tamanho por imagem enviada à plataforma. */
export const TAMANHO_MAXIMO_PADRAO = 3 * 1024 * 1024;

/** Maior lado aceito em pixels — 2560px cobre com folga o uso em relatório. */
export const LADO_MAIOR_PADRAO = 2560;

/** Qualidades JPEG tentadas em ordem, da melhor para a mais agressiva. */
const QUALIDADES = [0.85, 0.75, 0.65, 0.55, 0.45, 0.35];

/** Quando nem a menor qualidade couber no teto, reduz as dimensões e tenta de novo. */
const FATOR_REDUCAO = 0.75;
const MAX_TENTATIVAS_ESCALA = 4;

export interface OpcoesCompressao {
  /** Padrão: 5 MB. */
  tamanhoMaximoBytes?: number;
  /** Padrão: 2560 px. */
  ladoMaiorMaximo?: number;
}

function paraBlob(canvas: HTMLCanvasElement, qualidade: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', qualidade));
}

/** Decodifica o arquivo respeitando a orientação EXIF (foto de celular vem girada). */
async function carregarImagem(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Navegadores antigos não aceitam a opção: cai no fallback com <img>.
    }
  }

  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Não foi possível ler a imagem.'));
    };
    img.src = url;
  });
}

function dimensoesDe(imagem: ImageBitmap | HTMLImageElement): { largura: number; altura: number } {
  if ('naturalWidth' in imagem) {
    return { largura: imagem.naturalWidth, altura: imagem.naturalHeight };
  }
  return { largura: imagem.width, altura: imagem.height };
}

function liberarImagem(imagem: ImageBitmap | HTMLImageElement): void {
  if ('close' in imagem && typeof imagem.close === 'function') imagem.close();
}

/**
 * Desenha a imagem no tamanho final. A redução é feita em etapas de no máximo
 * metade por vez — um downscale único de 4000px para 800px serrilha a foto.
 */
function desenharRedimensionada(
  imagem: ImageBitmap | HTMLImageElement,
  larguraOrigem: number,
  alturaOrigem: number,
  larguraDestino: number,
  alturaDestino: number
): HTMLCanvasElement | null {
  let origem: CanvasImageSource = imagem;
  let largura = larguraOrigem;
  let altura = alturaOrigem;

  while (largura > larguraDestino * 2 && altura > alturaDestino * 2) {
    const proximaLargura = Math.max(larguraDestino, Math.round(largura / 2));
    const proximaAltura = Math.max(alturaDestino, Math.round(altura / 2));

    const intermediario = document.createElement('canvas');
    intermediario.width = proximaLargura;
    intermediario.height = proximaAltura;
    const ctxIntermediario = intermediario.getContext('2d');
    if (!ctxIntermediario) return null;
    ctxIntermediario.imageSmoothingEnabled = true;
    ctxIntermediario.imageSmoothingQuality = 'high';
    ctxIntermediario.drawImage(origem, 0, 0, proximaLargura, proximaAltura);

    origem = intermediario;
    largura = proximaLargura;
    altura = proximaAltura;
  }

  const canvas = document.createElement('canvas');
  canvas.width = larguraDestino;
  canvas.height = alturaDestino;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // JPEG não guarda transparência: sem o fundo branco, áreas transparentes viram preto.
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, larguraDestino, alturaDestino);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(origem, 0, 0, larguraDestino, alturaDestino);

  return canvas;
}

function trocarExtensaoParaJpg(nome: string): string {
  const semExtensao = nome.replace(/\.[^./\\]+$/, '');
  return `${semExtensao || 'imagem'}.jpg`;
}

/**
 * Reduz a imagem até caber no teto de tamanho, redimensionando e reencodando em JPEG.
 *
 * Passa o arquivo adiante sem tocar quando já está dentro do teto e das dimensões,
 * quando é SVG (vetor) ou quando não foi possível decodificar — nesses casos o
 * original segue para o backend, que continua validando o envio.
 */
export async function comprimirImagem(arquivo: File, opcoes: OpcoesCompressao = {}): Promise<File> {
  const tamanhoMaximo = opcoes.tamanhoMaximoBytes ?? TAMANHO_MAXIMO_PADRAO;
  const ladoMaximo = opcoes.ladoMaiorMaximo ?? LADO_MAIOR_PADRAO;

  // HEIC/HEIF (iPhone) precisa virar JPEG antes: o canvas não decodifica HEIC.
  const file = await converterHeicParaJpeg(arquivo);

  const tipo = file.type?.toLowerCase() ?? '';
  if (tipo && !tipo.startsWith('image/')) return file;
  if (tipo === 'image/svg+xml') return file;
  // GIF só é convertido se estourar o teto — reencodar perde a animação.
  if (tipo === 'image/gif' && file.size <= tamanhoMaximo) return file;

  let imagem: ImageBitmap | HTMLImageElement;
  try {
    imagem = await carregarImagem(file);
  } catch (error) {
    console.error('Falha ao decodificar imagem para compressão:', error);
    return file;
  }

  try {
    const { largura: larguraOriginal, altura: alturaOriginal } = dimensoesDe(imagem);
    if (!larguraOriginal || !alturaOriginal) return file;

    const maiorLado = Math.max(larguraOriginal, alturaOriginal);
    const precisaRedimensionar = maiorLado > ladoMaximo;

    // Já está pequena o bastante: não reencoda à toa (evita perder qualidade sem ganho).
    if (!precisaRedimensionar && file.size <= tamanhoMaximo) return file;

    let escala = precisaRedimensionar ? ladoMaximo / maiorLado : 1;
    let menorBlob: Blob | null = null;

    for (let tentativa = 0; tentativa < MAX_TENTATIVAS_ESCALA; tentativa++) {
      const largura = Math.max(1, Math.round(larguraOriginal * escala));
      const altura = Math.max(1, Math.round(alturaOriginal * escala));

      const canvas = desenharRedimensionada(imagem, larguraOriginal, alturaOriginal, largura, altura);
      if (!canvas) break;

      for (const qualidade of QUALIDADES) {
        const blob = await paraBlob(canvas, qualidade);
        if (!blob) continue;
        menorBlob = blob;
        if (blob.size <= tamanhoMaximo) {
          // Reencodar pode inchar uma imagem já bem comprimida: aí o original ocupa menos.
          if (blob.size >= file.size && file.size <= tamanhoMaximo) return file;
          return new File([blob], trocarExtensaoParaJpg(file.name), {
            type: 'image/jpeg',
            lastModified: file.lastModified,
          });
        }
      }

      escala *= FATOR_REDUCAO;
    }

    // Nenhuma tentativa coube no teto: fica com a menor gerada, se ela ajudar.
    if (menorBlob && menorBlob.size < file.size) {
      console.warn(
        `Imagem "${file.name}" não coube em ${(tamanhoMaximo / 1024 / 1024).toFixed(0)}MB; ` +
          `enviada com ${(menorBlob.size / 1024 / 1024).toFixed(1)}MB.`
      );
      return new File([menorBlob], trocarExtensaoParaJpg(file.name), {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      });
    }

    return file;
  } finally {
    liberarImagem(imagem);
  }
}

/**
 * Comprime uma lista de imagens em sequência (paralelo estoura a memória em
 * lotes grandes). `onProgresso` recebe quantas já foram processadas.
 */
export async function comprimirImagens(
  arquivos: File[],
  opcoes: OpcoesCompressao = {},
  onProgresso?: (processadas: number, total: number) => void
): Promise<File[]> {
  const resultado: File[] = [];

  for (let i = 0; i < arquivos.length; i++) {
    try {
      resultado.push(await comprimirImagem(arquivos[i], opcoes));
    } catch (error) {
      console.error(`Erro ao comprimir "${arquivos[i]?.name}":`, error);
      resultado.push(arquivos[i]);
    }
    onProgresso?.(i + 1, arquivos.length);
  }

  return resultado;
}

/** Comprime e devolve em base64 (data URL), para os fluxos que guardam a foto no estado. */
export async function comprimirImagemParaDataUrl(
  arquivo: File,
  opcoes: OpcoesCompressao = {}
): Promise<string> {
  const comprimido = await comprimirImagem(arquivo, opcoes);

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Erro ao ler o arquivo de imagem.'));
    reader.readAsDataURL(comprimido);
  });
}

/** Converte uma data URL em File, sem depender do serviço de API. */
export async function dataUrlParaFile(dataUrl: string, nome: string): Promise<File> {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], nome, { type: blob.type || 'image/jpeg' });
}

/** Comprime uma imagem já em base64 e devolve outra data URL dentro do teto. */
export async function comprimirDataUrl(dataUrl: string, opcoes: OpcoesCompressao = {}): Promise<string> {
  if (!dataUrl.startsWith('data:image/')) return dataUrl;
  const file = await dataUrlParaFile(dataUrl, 'imagem.jpg');
  return await comprimirImagemParaDataUrl(file, opcoes);
}
